import { useQuery } from '@vue/apollo-composable';
import { computed } from 'vue';

import { EntityType } from '@/__generated__/gateway/graphql';
import { MoneyV2 } from '@/__generated__/storefront/graphql.ts';
import {
  Metafield,
  PatternProduct,
  PatternProductsQueryResult,
  PatternProductsQueryVariables,
  PriceRanges,
  Variant
} from '@/shared/composables/patternProducts/patternProducts.types';

import {
  patternProductVariantsQuery,
  patternVariantsAndItsMetafield
} from './patternProductsStorefront.queries.ts';

export const usePatternProducts = (
  patternId: string,
  patternProductsList?: PatternProduct[] | null
) => {
  const { result: variantsResult } = useQuery(
    patternVariantsAndItsMetafield,
    { patternId: `gid://shopify/Product/${patternId}` }, // TODO use shopifyIdToGid
    { clientId: 'storefront' }
  );

  const patternVariants = computed(
    () => variantsResult?.value?.product?.variants?.nodes || []
  );

  const productIds = computed(() => {
    if (!patternProductsList) return [];
    return patternProductsList.map((pp) => pp.product_id);
  });

  const { result: resultPatternProducts } = useQuery<
    PatternProductsQueryResult,
    PatternProductsQueryVariables
  >(
    patternProductVariantsQuery,
    {
      ids: productIds.value,
      country: window?.Shopify?.country
    },
    {
      clientId: 'storefront'
    }
  );

  const hydratedProductList = computed(
    () => resultPatternProducts.value?.nodes?.filter((a) => a) ?? []
  );

  const getProductSubtitle = (metafields: Metafield[]) => {
    const parsedMetafields = metafields.reduce<Record<string, string>>(
      (metaFieldsObject, metafield) => {
        if (metafield) {
          metaFieldsObject[metafield.key] = metafield.value;
        }
        return metaFieldsObject;
      },
      {}
    );

    const isPattern = parsedMetafields['entity_type'] === EntityType.Pattern;

    const tag = isPattern
      ? parsedMetafields['pattern_kind']
      : parsedMetafields['yarn_weight_category'];

    const subtitle = isPattern
      ? parsedMetafields['designer']
      : parsedMetafields['subtitle'];

    return tag || subtitle;
  };

  const getPrices = (variant: Variant, priceRanges: PriceRanges) => {
    if (variant) {
      return {
        price: variant.price,
        compareAtPrice: variant.compareAtPrice
      };
    }

    return {
      price: priceRanges.price.minVariantPrice,
      compareAtPrice: priceRanges.compareAtPrice.minVariantPrice
    };
  };

  const getDiscountPercentage = (price: MoneyV2, compareAtPrice: MoneyV2) => {
    const oldPrice = parseFloat(compareAtPrice?.amount);
    if (oldPrice && oldPrice > 0) {
      return Math.floor(
        ((oldPrice - parseFloat(price.amount)) * 100) / oldPrice
      );
    }

    return 0;
  };

  return {
    hydratedProductList,
    patternVariants,
    getProductSubtitle,
    getPrices,
    getDiscountPercentage
  };
};
